import styled from "@emotion/styled";
import { Skeleton } from "@mui/material";
import { Navigate, useLocation, useNavigate, useParams } from "react-router";
import { Helmet } from "react-helmet-async";
import { useGetSubscriptionFlow } from "@queries/subscription-flow/useGetSubscriptionFlow";
import { useEffect, useMemo } from "react";
import { toast } from "@shared/services/toast";
import { useAppName } from "@shared/hooks/useAppName";
import { SubscriptionPageLayout } from "@shared/layouts/SubscriptionPageLayout";
import { isUuid } from "../../shared/utils/validators";
import { SubscriptionRegistrationFlow } from "@features/Subscription/SubscriptionRegistrationFlow";
import { StartSubscriptionFlow } from "@features/Subscription/StartSubscriptionFlow";
import { SubscriptionFlowSkeleton } from "@features/Subscription/components/SubscriptionFlowSkeleton";
import { useSearchParams } from "react-router-dom";
import { convertSubscriptionFlowStateToSubscriptionFlowProps } from "./subscription-registration-helpers";
import { PriceBreakdown } from "@features/Subscription/components/PriceBreakdown";
import { SubscriptionOnboardingHero } from "@features/Subscription/components/SubscriptionOnboardingHero";
import { RadarMarketingSection } from "@features/Subscription/components/RadarMarketingSection/RadarMarketingSection";
import { RadarMarketingFooter } from "@features/Subscription/components/RadarMarketingFooter/RadarMarketingFooter";
import { ThemeProvider } from "@shared/providers/ThemeProvider";
import { useHandleSSOErrors } from "@shared/hooks/useHandleSSOErrors";
import { appEventEmitter } from "@shared/events/app-event-emitter";
import { SubscriptionFlowStatus } from "@features/Subscription/subscription.types";
import { useGetMembershipAffiliations } from "@queries/subscription-flow/useGetMembershipAffiliations";
import { useGetMembershipAffiliationPrice } from "@queries/subscription-flow/useGetMembershipAffiliationPrice";
import {
  INDIVIDUAL_PLAN,
  INDIVIDUAL_PLAN_ID,
} from "@features/Subscription/individual-plan";

const INDIVIDUAL_HERO_HEADLINE = "Know who to reach out to and why now.";
const INDIVIDUAL_HERO_BODY =
  "Radar finds important updates from your contacts' social media posts and suggests what to say to stay top of mind.";

const Container = styled.div`
  width: 100%;
  margin: 0 auto;

  ${(p) => p.theme.media.mobileDown} {
    padding: 0;
  }
`;

type Props = {
  showMlsPicker?: boolean;
};

export const SubscriptionRegistration = ({ showMlsPicker = false }: Props) => {
  const {
    partnerId,
    subscriptionId = "",
    token,
  } = useParams<{
    partnerId: string;
    subscriptionId: string;
    token: string;
  }>();
  const navigate = useNavigate();
  const error = useHandleSSOErrors();
  const { data: affiliations = [] } = useGetMembershipAffiliations();
  const appName = useAppName();

  useEffect(() => {
    if (!partnerId) return;
    if (partnerId === INDIVIDUAL_PLAN_ID) return;
    if (partnerId === "independent") {
      navigate("/subscribe", { replace: true });
      return;
    }
    if (
      affiliations.length > 0 &&
      !affiliations.some((a) => a.id === partnerId)
    ) {
      navigate("/subscribe", { replace: true });
    }
  }, [partnerId, affiliations, navigate]);

  useEffect(() => {
    if (error) {
      appEventEmitter.emit({
        eventType: "sidekick-registration-start-failure",
      });
    }
  }, [error]);

  useEffect(() => {
    if (subscriptionId && !isUuid(subscriptionId)) {
      toast.error("Subscription not found.", {
        autoClose: 7000,
        toastId: "subscription-not-found",
      });
      navigate("/subscribe");
    }
  }, [subscriptionId, navigate]);

  const subscriptionFlowQuery = useGetSubscriptionFlow({
    id: isUuid(subscriptionId) ? subscriptionId : "",
    token,
    onError: (error) => {
      if (error.status >= 400 && error.status < 500) {
        toast.error("Subscription not found.", {
          autoClose: 7000,
          toastId: "subscription-not-found",
        });
        navigate("/subscribe");
        return;
      }
      toast.error("Something went wrong. Please try again later.", {
        autoClose: 7000,
        toastId: "subscription-flow-error",
      });
      navigate("/subscribe");
    },
    onSuccess: (data) => {
      const status = data.status as SubscriptionFlowStatus;
      if (
        status !== "trial-started" &&
        status !== "subscription-started" &&
        localStorage.getItem("@sidekick:registration-start-pending") === "true"
      ) {
        appEventEmitter.emit({
          eventType: "sidekick-registration-start-success",
        });
        localStorage.removeItem("@sidekick:registration-start-pending");
      }
    },
  });

  const partner = useMemo(() => {
    const resolvedId =
      partnerId ??
      (subscriptionFlowQuery.data?.planInfo
        ? (subscriptionFlowQuery.data.planInfo.id as string)
        : null);
    if (!resolvedId) return null;
    if (resolvedId === INDIVIDUAL_PLAN_ID) return INDIVIDUAL_PLAN;
    return affiliations.find((a) => a.id === resolvedId) ?? null;
  }, [partnerId, subscriptionFlowQuery.data, affiliations]);

  const subscriptionFlowProps = useMemo(
    () =>
      subscriptionFlowQuery.data &&
      convertSubscriptionFlowStateToSubscriptionFlowProps({
        data: subscriptionFlowQuery.data,
        partner,
      }),
    [subscriptionFlowQuery.data, partner],
  );

  const subscriptionInfo = subscriptionFlowProps?.subscriptionInfo;
  const planInfo = subscriptionFlowProps?.planInfo;

  const isInitialIndividualStep =
    !subscriptionId && partnerId === INDIVIDUAL_PLAN_ID;
  const individualPriceQuery = useGetMembershipAffiliationPrice({
    planId: INDIVIDUAL_PLAN_ID,
    enabled: isInitialIndividualStep,
  });

  const onboardingHero = useMemo<{
    eyebrow?: string;
    headline: string;
    body?: string;
  }>(() => {
    if (subscriptionId && partner) {
      const isIndividual = partner.id === INDIVIDUAL_PLAN_ID;
      const trialDays =
        subscriptionInfo?.availableOffer?.trialDays || planInfo?.trialDays;
      return {
        eyebrow: isIndividual ? undefined : `${partner.name} Subscription`,
        headline: isIndividual
          ? INDIVIDUAL_HERO_HEADLINE
          : trialDays
            ? `Try Sidekick free for ${trialDays} days.`
            : "Your Sidekick subscription is ready.",
        body: isIndividual ? INDIVIDUAL_HERO_BODY : undefined,
      };
    }
    if (partnerId === INDIVIDUAL_PLAN_ID) {
      return {
        headline: INDIVIDUAL_HERO_HEADLINE,
        body: INDIVIDUAL_HERO_BODY,
      };
    }
    if (partnerId || showMlsPicker) {
      return {
        eyebrow: "Organization Access",
        headline: "Your account may already be waiting.",
      };
    }
    return {
      eyebrow: "Create Your Account",
      headline: "Start with the path that's right for you.",
    };
  }, [
    subscriptionId,
    partner,
    subscriptionInfo,
    planInfo,
    partnerId,
    showMlsPicker,
  ]);

  return (
    <>
      <Helmet>
        <title>{appName} Subscription</title>
      </Helmet>
      <SubscriptionPageLayout
        theme="light"
        rightMaxWidth={subscriptionId ? "600px" : "540px"}
        hideSupportButton={partner?.id === INDIVIDUAL_PLAN_ID}
        fillViewport={partner?.id !== INDIVIDUAL_PLAN_ID}
        leftContent={
          <SubscriptionOnboardingHero
            eyebrow={onboardingHero.eyebrow}
            headline={onboardingHero.headline}
            body={onboardingHero.body}
          >
            {(!!subscriptionId || isInitialIndividualStep) && (
              <ThemeProvider defaultTheme="dark">
                {!!subscriptionId && (
                  <PriceBreakdown
                    price={subscriptionInfo?.price}
                    productName={"Sidekick Subscription"}
                    productDescription={
                      "The leading AI assistant purpose-built to help agents and brokerages save time and grow."
                    }
                    currency={subscriptionInfo?.currency}
                    offer={subscriptionInfo?.availableOffer}
                    planName={planInfo?.name ?? ""}
                    planTrialDays={planInfo?.trialDays ?? 0}
                    partner={partner}
                    creditCardRequired={
                      subscriptionInfo?.creditCardRequired ?? true
                    }
                    hideMarketingCopy
                  />
                )}
                {isInitialIndividualStep && individualPriceQuery.isLoading && (
                  <Skeleton
                    variant="rounded"
                    sx={{ width: "100%", height: 142, maxWidth: 440 }}
                  />
                )}
                {isInitialIndividualStep && individualPriceQuery.data && (
                  <PriceBreakdown
                    price={individualPriceQuery.data.monthlyPrice}
                    productName={"Sidekick Subscription"}
                    productDescription={
                      "The leading AI assistant purpose-built to help agents and brokerages save time and grow."
                    }
                    planName={INDIVIDUAL_PLAN.name}
                    planTrialDays={individualPriceQuery.data.trialDays}
                    partner={INDIVIDUAL_PLAN}
                    creditCardRequired
                    hideMarketingCopy
                  />
                )}
              </ThemeProvider>
            )}
          </SubscriptionOnboardingHero>
        }
        rightContent={
          <Container>
            {!subscriptionId && (
              <StartSubscriptionFlow
                defaultValues={{ partnerId }}
                showMlsPicker={showMlsPicker}
              />
            )}

            {!!subscriptionId && subscriptionFlowQuery.status === "pending" && (
              <SubscriptionFlowSkeleton />
            )}

            {!!subscriptionId && !!subscriptionFlowProps && (
              <SubscriptionRegistrationFlow {...subscriptionFlowProps} />
            )}
          </Container>
        }
      />
      {partner?.id === INDIVIDUAL_PLAN_ID && (
        <>
          <RadarMarketingSection />
          <RadarMarketingFooter />
        </>
      )}
    </>
  );
};

export const RedirectToSubscriptionRegistrationPage = () => {
  const [searchParams] = useSearchParams();
  const { subscriptionId, token } = useParams<{
    subscriptionId?: string;
    token?: string;
  }>();

  const mls = searchParams.get("mls")?.trim() ?? "";

  if (mls) {
    const path = [mls, subscriptionId, token].filter(Boolean).join("/");
    return <Navigate to={`/subscribe/partner/${path}`} replace />;
  }

  return <SubscriptionRegistration showMlsPicker />;
};

export const RedirectToSubscribe = () => {
  const location = useLocation();
  return <Navigate to={`/subscribe${location.search}`} />;
};
